import styled, {css} from 'styled-components';

import {mixins} from '../../styles';

type TDateProps = {
  isRound?: boolean;
  disabled?: boolean;
  isHovered?: boolean;
  isEndHovered?: boolean;
  isStartHovered?: boolean;
};

type TDateStatusProps = {
  isRound?: boolean;
  disabled?: boolean;
  isNow?: boolean;
  isSelected?: boolean;
  isHovered?: boolean;
};

const getDateColor = ({theme, disabled, isSelected}: TDateStatusProps & TTheme) => {
  if (disabled) {
    return theme.palette.grey400;
  }

  if (isSelected) {
    return theme.palette.white;
  }

  return theme.palette.grey900;
};

const getDateBorderColor = ({theme, isNow, isSelected}: TDateStatusProps & TTheme) => {
  if (isSelected) {
    return theme.palette.grey100;
  }

  if (isNow) {
    return theme.palette.grey400;
  }

  return 'transparent';
};

const getDateBackground = ({theme, isSelected, isHovered}: TDateStatusProps & TTheme) => {
  if (isSelected) {
    return theme.branding.content.primary;
  }

  if (isHovered) {
    return theme.palette.grey100;
  }

  return 'transparent';
};

export const EmptyDate = styled.div<{isRound?: boolean}>`
  ${({theme, isRound}) =>
    isRound ? mixins.wh(`${theme.dimensions.controlHeightM}px`) : mixins.wh('80px', '34px')};
`;

export const DateStatus = styled.div<TDateStatusProps>`
  ${mixins.stack({align: 'center', justify: 'center'})};

  border-style: solid;

  ${({theme, isSelected, isRound}) => css`
    ${mixins.wh(`100%`)};
    transition: all ${theme.animation.duration / 2}ms;
    border-color: ${getDateBorderColor};
    border-width: ${theme.spacing.s05};
    border-radius: ${isRound ? '50%' : '32px'};
    color: ${getDateColor};
    background-color: ${getDateBackground};

    &:hover {
      background-color: ${isSelected ? theme.branding.content.primary : theme.palette.grey100};
    }
  `}
`;

const getDateBorderRadius = ({isEndHovered, isStartHovered, isRound}: TDateProps & TTheme) => {
  if (isEndHovered) {
    return isRound ? '0 50% 50% 0' : '0 32px 32px 0';
  }

  if (isStartHovered) {
    return isRound ? '50% 0 0 50%' : '32px 0 0 32px';
  }

  return 0;
};

export const Date = styled.div<TDateProps>`
  border-style: solid;
  border-width: 1px;

  ${({theme, disabled, isHovered, isRound}) => css`
    ${isRound ? mixins.wh(`${theme.dimensions.controlHeightM}px`) : mixins.wh('100%', '34px')};
    transition: all ${theme.animation.duration / 2}ms;
    border-color: ${isHovered ? theme.palette.grey100 : 'transparent'};
    border-radius: ${getDateBorderRadius};
    cursor: ${disabled ? 'default' : 'pointer'};
    color: ${getDateColor};
    pointer-events: ${disabled ? 'none' : 'auto'};
    background-color: ${isHovered ? theme.palette.grey100 : 'transparent'};
  `}
`;

export const WeekDay = styled.div`
  ${mixins.stack({align: 'center', justify: 'center'})};
  ${mixins.f2};
  ${({theme}) => mixins.wh(`${theme.dimensions.controlHeightM}px`, '28px')};
  color: ${({theme}) => theme.palette.grey400};
  font-weight: 400;
`;

export const Month = styled.div`
  ${mixins.stack({direction: 'COLUMN', align: 'start', justify: 'start', size: '05'})};
`;

export const MonthRow = styled.div`
  ${mixins.stack({direction: 'ROW', align: 'center', justify: 'center'})};
`;

export const DateGrid = styled.div`
  display: grid;
  grid-template-rows: repeat(auto-fill, 60px);
  grid-template-columns: repeat(3, 1fr);
  width: 100%;
  min-height: 214px;
`;

export const DateWrapper = styled.div<{disabled?: boolean}>`
  padding: 13px 0;
  cursor: ${({disabled}) => (disabled ? 'default' : 'pointer')};
  pointer-events: ${({disabled}) => (disabled ? 'none' : 'auto')};
`;
